import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';
import { calculateIsAtBottom } from './onboardingScroll';

// Animation duration for smooth scrolling (ms)
// 300ms matches project CSS transitions and feels smooth without being sluggish
const SCROLL_ANIMATION_DURATION = 300;

/**
 * Easing functions for scroll animations
 *
 * easeInOutCubic: Smooth acceleration AND deceleration (zero velocity at start & end)
 * - Prevents the "jerky start" that easeOut functions have
 * - Feels more natural for scroll animations
 *
 * easeOutQuart: Fast start, smooth deceleration (for snappy feel when needed)
 */
const EASING = {
  // Smooth start and end - best for general scroll animations
  easeInOutCubic: (t: number): number =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2,

  // Smooth deceleration only - for when you want responsive feel
  easeOutQuart: (t: number): number => 1 - Math.pow(1 - t, 4),
} as const;

/**
 * Animates scroll to a target position with easing
 *
 * Uses easeInOutCubic by default for smooth start AND end.
 * Cancels any in-progress animation to prevent jittery stacking.
 *
 * @param activeAnimationFrameIdRef - Ref to track the active animation frame ID for cancellation
 */
function animateScrollTo(
  container: HTMLElement,
  targetTop: number,
  activeAnimationFrameIdRef: React.MutableRefObject<number | null>,
  duration: number = SCROLL_ANIMATION_DURATION,
  easingFn: (t: number) => number = EASING.easeInOutCubic
) {
  // Cancel any in-progress animation to prevent fighting/jitter
  if (activeAnimationFrameIdRef.current !== null) {
    window.cancelAnimationFrame(activeAnimationFrameIdRef.current);
    activeAnimationFrameIdRef.current = null;
  }

  const startTop = container.scrollTop;
  const distance = targetTop - startTop;
  if (Math.abs(distance) < 1) return;

  const startTime = performance.now();

  const step = (now: number) => {
    const elapsed = now - startTime;
    const progress = Math.min(1, elapsed / duration);
    container.scrollTop = startTop + distance * easingFn(progress);

    if (progress < 1) {
      activeAnimationFrameIdRef.current = window.requestAnimationFrame(step);
    } else {
      activeAnimationFrameIdRef.current = null;
    }
  };

  activeAnimationFrameIdRef.current = window.requestAnimationFrame(step);
}

interface UseOnboardingScrollOptions {
  /**
   * Ref to the composer element - kept for potential future use
   */
  composerRef?: RefObject<HTMLDivElement | null>;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function useOnboardingScroll(_options: UseOnboardingScrollOptions = {}) {
  const chatEndRef = useRef<HTMLDivElement | null>(null);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);

  // Track if user is at the bottom of the chat
  // This is the core state for the chat-behaviour-guidelines
  const [isAtBottom, setIsAtBottom] = useState(true);

  // Flag to temporarily ignore scroll events during programmatic scrolling
  const isScrollingProgrammaticallyRef = useRef(false);

  // Track active animation frame ID for cancellation (per-instance to avoid cross-instance conflicts)
  const activeAnimationFrameIdRef = useRef<number | null>(null);

  // Track setTimeout IDs for cleanup on unmount (prevents memory leaks)
  const timeoutIdsRef = useRef<number[]>([]);

  /**
   * Cleanup effect: Cancel animation frames and clear timeouts on unmount
   * This prevents memory leaks and state updates on unmounted components
   */
  useEffect(() => {
    return () => {
      // Cancel any in-progress animation
      if (activeAnimationFrameIdRef.current !== null) {
        window.cancelAnimationFrame(activeAnimationFrameIdRef.current);
      }
      // Clear all tracked timeouts
      timeoutIdsRef.current.forEach(clearTimeout);
    };
  }, []);

  /**
   * Check if user is currently at bottom and update state
   */
  const checkIsAtBottom = useCallback(() => {
    if (!chatContainerRef.current) return true;
    return calculateIsAtBottom(chatContainerRef.current);
  }, []);

  /**
   * Handle scroll events to track isAtBottom state
   * Per guidelines section 2.2: update isAtBottom when user scrolls
   */
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      // Don't update state during programmatic scrolling to avoid flicker
      if (isScrollingProgrammaticallyRef.current) {
        return;
      }

      const atBottom = calculateIsAtBottom(container);
      setIsAtBottom(atBottom);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    // Initial check
    handleScroll();

    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  /**
   * Scroll to the bottom of the chat
   * With flex layout, scroll container is properly sized - just scroll to max
   *
   * Uses requestAnimationFrame to ensure DOM has updated before calculating scroll position.
   * This is critical when content/composer size changes.
   */
  const scrollToBottom = useCallback(() => {
    const container = chatContainerRef.current;
    if (!container) {
      return;
    }

    // Wait for DOM to update before calculating scroll position
    // This is critical when content/composer size changes
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Double RAF ensures layout has completed
        if (!chatContainerRef.current) return;
        const container = chatContainerRef.current;

        isScrollingProgrammaticallyRef.current = true;

        const currentScrollTop = container.scrollTop;
        const targetScrollTop = container.scrollHeight - container.clientHeight;
        const finalScrollTop = Math.max(0, targetScrollTop);

        // Only animate if there's actual distance to scroll
        if (Math.abs(finalScrollTop - currentScrollTop) >= 1) {
          animateScrollTo(container, finalScrollTop, activeAnimationFrameIdRef);
        } else {
          isScrollingProgrammaticallyRef.current = false;
          setIsAtBottom(true);
          return;
        }

        // Reset flag after animation completes and set isAtBottom
        const timeoutId = window.setTimeout(() => {
          isScrollingProgrammaticallyRef.current = false;
          setIsAtBottom(true);
        }, SCROLL_ANIMATION_DURATION + 50);
        timeoutIdsRef.current.push(timeoutId);
      });
    });
  }, []);

  /**
   * Conditionally scroll to bottom only if user WAS at bottom
   * Per guidelines section 3.1: auto-scroll only when user is at bottom
   *
   * IMPORTANT: Uses the `isAtBottom` STATE (updated by scroll events) not a fresh calculation.
   * This is because when new content appears, scrollHeight increases but scrollTop stays same,
   * so a fresh calculation would show user NOT at bottom even if they were just there.
   */
  const scrollToBottomIfAtBottom = useCallback(() => {
    if (!chatContainerRef.current) {
      return;
    }

    // Use the STORED state - this reflects where user was BEFORE content changed
    if (isAtBottom) {
      scrollToBottom();
    }
  }, [scrollToBottom, isAtBottom]);

  /**
   * Scroll to a specific element within the container
   */
  const scrollToElement = useCallback((element: HTMLElement | null) => {
    const container = chatContainerRef.current;
    if (!container || !element) return;

    isScrollingProgrammaticallyRef.current = true;

    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    const offset = 24; // Padding from top
    const distanceFromTopWithinContainer = elementRect.top - containerRect.top;
    const delta = distanceFromTopWithinContainer - offset;

    if (Math.abs(delta) < 1) {
      isScrollingProgrammaticallyRef.current = false;
      return;
    }

    const targetScrollTop = Math.max(container.scrollTop + delta, 0);
    animateScrollTo(container, targetScrollTop, activeAnimationFrameIdRef);

    const timeoutId = window.setTimeout(() => {
      isScrollingProgrammaticallyRef.current = false;
      // Update isAtBottom after scroll completes
      setIsAtBottom(calculateIsAtBottom(container));
    }, SCROLL_ANIMATION_DURATION + 50);
    timeoutIdsRef.current.push(timeoutId);
  }, []);

  const scrollToElementWithDelay = useCallback(
    (element: HTMLElement | null, delayMs: number = 30) => {
      const timeoutId = window.setTimeout(() => {
        scrollToElement(element);
      }, delayMs);
      timeoutIdsRef.current.push(timeoutId);
    },
    [scrollToElement]
  );

  /**
   * Scrolls to the top of the chat container
   * Used when showing large composer UIs where we want user to see message history
   */
  const scrollToTop = useCallback(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    isScrollingProgrammaticallyRef.current = true;
    animateScrollTo(container, 0, activeAnimationFrameIdRef);

    const timeoutId = window.setTimeout(() => {
      isScrollingProgrammaticallyRef.current = false;
      setIsAtBottom(false); // User is now reading history
    }, SCROLL_ANIMATION_DURATION + 50);
    timeoutIdsRef.current.push(timeoutId);
  }, []);

  /**
   * Handle iOS keyboard via visualViewport API
   *
   * NOTE: Layout is now handled by CSS dvh units (configured in tailwind.config.js).
   * The h-screen class uses ['100vh', '100dvh'] which automatically adjusts when
   * the mobile keyboard appears/disappears.
   *
   * This effect handles SCROLL BEHAVIOR only:
   * - When keyboard appears with a focused input → scroll to keep it visible
   * - When keyboard appears/disappears and user was at bottom → scroll to stay at bottom
   * - When user is reading history → preserve their position
   *
   * CRITICAL: We use a ref to track the isAtBottom state at the time of the PREVIOUS
   * viewport size. By the time handleResize fires, container dimensions have already
   * changed, making a fresh calculation unreliable.
   */
  // Track isAtBottom state for visualViewport handler (using ref to avoid stale closures)
  const isAtBottomRef = useRef(isAtBottom);
  useEffect(() => {
    isAtBottomRef.current = isAtBottom;
  }, [isAtBottom]);

  useEffect(() => {
    // visualViewport is available on iOS 13+ and modern browsers
    const viewport = window.visualViewport;
    if (!viewport) return;

    let prevHeight = viewport.height;

    const handleResize = () => {
      const container = chatContainerRef.current;
      if (!container) return;

      const newHeight = viewport.height;
      const heightDelta = newHeight - prevHeight;
      prevHeight = newHeight;

      // Skip small changes (avoid micro-adjustments)
      if (Math.abs(heightDelta) < 10) return;

      const keyboardAppeared = heightDelta < 0;
      const keyboardDismissed = heightDelta > 0;

      // Use the STORED isAtBottom state (captured before resize) - NOT a fresh calculation
      const wasAtBottom = isAtBottomRef.current;

      // Per guidelines section 7.2-7.3:
      // When keyboard appears/disappears, adjust scroll based on at-bottom state
      if (keyboardAppeared) {
        // Keyboard appeared (viewport shrunk)

        // Check if an input is focused
        const focusedElement = document.activeElement as HTMLElement | null;
        const isInputFocused =
          focusedElement &&
          (focusedElement.tagName === 'INPUT' ||
            focusedElement.tagName === 'TEXTAREA' ||
            focusedElement.isContentEditable);

        if (isInputFocused && focusedElement) {
          // Wait for layout to settle (CSS dvh adjustment), then handle scroll
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (!chatContainerRef.current) return;

              // Check if focused element is INSIDE the scroll container
              const isInsideScrollContainer =
                chatContainerRef.current.contains(focusedElement);

              if (isInsideScrollContainer) {
                // Element is inside scroll container (e.g., AddressStep)
                // Use scrollIntoView to keep it visible
                focusedElement.scrollIntoView({
                  behavior: 'smooth',
                  block: 'nearest',
                  inline: 'nearest',
                });
              } else {
                // Element is OUTSIDE scroll container (e.g., PlaceNameStep in composer)
                // Scroll the chat to bottom so user sees the latest messages above the composer
                const targetScrollTop =
                  chatContainerRef.current.scrollHeight -
                  chatContainerRef.current.clientHeight;
                chatContainerRef.current.scrollTop = Math.max(
                  0,
                  targetScrollTop
                );
              }
            });
          });
        } else if (wasAtBottom) {
          // No focused input, but user was at bottom - scroll to stay at bottom
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (!chatContainerRef.current) return;
              const targetScrollTop =
                chatContainerRef.current.scrollHeight -
                chatContainerRef.current.clientHeight;
              chatContainerRef.current.scrollTop = Math.max(0, targetScrollTop);
            });
          });
        }
        // If not at bottom and no focused input, preserve reading position (do nothing)
      } else if (keyboardDismissed && wasAtBottom) {
        // Keyboard dismissed and user was at bottom - scroll to stay at bottom
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!chatContainerRef.current) return;
            const targetScrollTop =
              chatContainerRef.current.scrollHeight -
              chatContainerRef.current.clientHeight;
            chatContainerRef.current.scrollTop = Math.max(0, targetScrollTop);
          });
        });
      }
      // If keyboard dismissed and user was NOT at bottom, preserve position (do nothing)
    };

    viewport.addEventListener('resize', handleResize);
    return () => viewport.removeEventListener('resize', handleResize);
  }, []);

  /**
   * Force update isAtBottom state (useful after step transitions)
   */
  const refreshIsAtBottom = useCallback(() => {
    if (!chatContainerRef.current) return;
    setIsAtBottom(calculateIsAtBottom(chatContainerRef.current));
  }, []);

  return {
    // Refs
    chatEndRef,
    chatContainerRef,

    // State
    isAtBottom,

    // Scroll functions
    scrollToBottom,
    scrollToBottomIfAtBottom,
    scrollToElement,
    scrollToElementWithDelay,
    scrollToTop,

    // Utilities
    checkIsAtBottom,
    refreshIsAtBottom,
  };
}
